import {
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';
import {
    signUpThunk,
    confirmSignUpThunk,
    setProfileDataThunk,
    confirmPhoneNumberThunk,
    checkSignUpThunk,
    updateUserAttributesThunk,
    resendEmailConfirmationCodeThunk,
} from './thunks';
import {
    State,
    SignUpStep,
    FetchStatus,
    EmployerInfo,
    SignUpMode,
} from './types';

const initialState: State = {
    email: '',
    editMode: false,
    phoneNumber: '',
    logoImage: null,
    legalModal: false,
    userId: undefined,
    employerInfo: null,
    fetchStatus: 'Idle',
    legalAgreement: false,
    disablePhoneNumber: false,
    signUpStep: 'registerEmail',
    signUpMode: 'Owner',
    personalEmailWarningModal: false,
};

export const signUpSlice = createSlice({
    name: 'signUp',
    initialState,
    reducers: {
        setSignUpStep: (state, action: PayloadAction<SignUpStep>) => {
            state.signUpStep = action.payload;
        },
        setEditMode: (state, action: PayloadAction<boolean>) => {
            state.editMode = action.payload;
        },
        setUserPhoneNumber: (state, action: PayloadAction<string>) => {
            state.phoneNumber = action.payload;
        },
        setUserEmail: (state, action: PayloadAction<string>) => {
            state.email = action.payload;
        },
        setFetchStatus: (state, action: PayloadAction<FetchStatus>) => {
            state.fetchStatus = action.payload;
        },
        setEmployerInfo: (state, action: PayloadAction<EmployerInfo | null>) => {
            state.employerInfo = action.payload;
        },
        setLogoImage: (state, action: PayloadAction<string | null>) => {
            state.logoImage = action.payload;
        },
        setLegalAgreement: (state, action: PayloadAction<boolean>) => {
            state.legalAgreement = action.payload;
        },
        setLegalModal: (state, action: PayloadAction<boolean>) => {
            state.legalModal = action.payload;
        },
        setSignUpUserId: (state, action: PayloadAction<string | undefined>) => {
            state.userId = action.payload;
        },
        setPersonalEmailWarningModal: (state, action: PayloadAction<boolean>) => {
            state.personalEmailWarningModal = action.payload;
        },
        setSignUpMode: (state, action: PayloadAction<SignUpMode>) => {
            state.signUpMode = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(checkSignUpThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(checkSignUpThunk.rejected, (state) => {
                state.fetchStatus = 'Idle';
            })
            .addCase(signUpThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(signUpThunk.fulfilled, (state, action) => {
                state.userId = action.payload;
                state.fetchStatus = 'Idle';
                state.signUpStep = 'verifyEmail';
            })
            .addCase(signUpThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(confirmSignUpThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(confirmSignUpThunk.fulfilled, (state) => {
                state.fetchStatus = 'Idle';
            })
            .addCase(confirmSignUpThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(setProfileDataThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(setProfileDataThunk.fulfilled, (state) => {
                state.fetchStatus = 'Idle';
                state.signUpStep = 'approval';
            })
            .addCase(setProfileDataThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(confirmPhoneNumberThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(confirmPhoneNumberThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(confirmPhoneNumberThunk.fulfilled, (state) => {
                state.fetchStatus = 'Idle';
                state.disablePhoneNumber = true;
                state.signUpStep = 'uploadLogo';
            })
            .addCase(updateUserAttributesThunk.fulfilled, (state, action) => {
                state.fetchStatus = 'Idle';

                if (action.payload) {
                    state.signUpStep = 'verifyPhoneNumber';
                } else {
                    state.signUpStep = state.editMode ? 'reviewInfo' : 'uploadLogo';
                }
            })
            .addCase(updateUserAttributesThunk.rejected, (state) => {
                state.fetchStatus = 'Rejected';
            })
            .addCase(updateUserAttributesThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(resendEmailConfirmationCodeThunk.pending, (state) => {
                state.fetchStatus = 'Pending';
            })
            .addCase(resendEmailConfirmationCodeThunk.fulfilled, (state) => {
                state.signUpStep = 'verifyEmail';
                state.fetchStatus = 'Idle';
            })
            .addCase(resendEmailConfirmationCodeThunk.rejected, (state) => {
                state.fetchStatus = 'Idle';
            })
    }
})

export const {
    setSignUpMode,
    setEditMode,
    setLogoImage,
    setUserEmail,
    setLegalModal,
    setSignUpStep,
    setFetchStatus,
    setEmployerInfo,
    setSignUpUserId,
    setLegalAgreement,
    setUserPhoneNumber,
    setPersonalEmailWarningModal,
} = signUpSlice.actions;